/* assets/rod-cart.js : cart drawer + cart page behaviour (WP-6, SPEC 5.11.1 / 5.11.2).
   Loaded once by the snippets/cart-products.liquid patch.

   Responsibilities (rod NEVER posts /cart/change.js itself and never calls renderSection):
   - Turkish quantity rules on cart lines, scoped to `cart-items-component .rod-cart__qty-input`
     (never `rod-qty` inputs, which belong to rod-buy.js).
   - Extends Horizon's `cart-items-disabled` lock to the keyboard and shows the busy state.
   - Remove snapshot + inline "Geri al" notice (drawer) / toast (/cart), undo through ONE
     standard CartLinesUpdateEvent so every cart listener updates itself.
   - Translates Horizon's /cart/change.js error text into Turkish (5.7.5 mapping).
   - Phone/tablet fixed checkout bar on /cart and the "Alışverişe Devam Et" back link.
*/
import { CartLinesUpdateEvent } from '@shopify/events';
import { Rod } from '@rod/core';

const doc = document;

const MSG_INVALID = 'Lütfen geçerli bir adet girin.';
const MSG_REMOVED = 'Ürün sepetten çıkarıldı.';
const MSG_RESTORED = 'Ürün tekrar sepete eklendi.';
const MSG_UNDO_FAILED = 'Ürün tekrar eklenemedi. Lütfen tekrar deneyin.';
const MSG_OFFLINE = 'Bağlantı sorunu nedeniyle ürün eklenemedi. Lütfen tekrar deneyin.';
const MSG_CLEARED = 'Sepetiniz boşaltıldı.';
const MSG_CLEAR_FAILED = 'Sepet boşaltılamadı. Lütfen tekrar deneyin.';

const QTY_INPUT = '.rod-cart__qty-input';
const GENERIC_ERRORS = [
  'cart error',
  'add to cart failed',
  'network error during add to cart',
  'failed to update cart',
  'cart update failed',
];

/* ------------------------------------------------------------------ helpers */
const routes = (Rod && Rod.config && Rod.config.routes) || {};
const cartRoute = routes.cart || '/cart';
const cartAddRoute = routes.cartAdd || '/cart/add';
const shopRoot = window.Shopify?.routes?.root || '/';
const cartClearRoute = `${shopRoot}cart/clear.js`;

/** @param {Element|null} el */
function componentOf(el) {
  return el ? el.closest('cart-items-component') : null;
}
/** @param {Element|null} el */
function rowOf(el) {
  return el ? el.closest('[data-rod-line]') : null;
}
/** @param {Element|null} component */
function isLocked(component) {
  return !!component && component.classList.contains('cart-items-disabled');
}
function anyLocked() {
  return !!doc.querySelector('cart-items-component.cart-items-disabled');
}

/** The drawer's inline notice, but only while the drawer dialog is actually open (D22). */
function openDrawerNotice() {
  const el = doc.querySelector('[data-rod-cart-notice]');
  if (!el) return null;
  const dialog = el.closest('dialog');
  return dialog && dialog.open ? el : null;
}

/* ------------------------------------------------------------------ per-line messages */
const ownWrites = new WeakSet();

function errorRefs(component, line) {
  if (!component || !line) return {};
  return {
    box: component.querySelector(`[ref="cartItemErrorContainer-${line}"]`),
    text: component.querySelector(`[ref="cartItemError-${line}"]`),
  };
}

/* A message shown right before a /cart/change.js round trip survives the section morph:
   bindAll() re-applies it to the same line once the new markup is in. */
let stickyMessage = null;

function showLineMessage(component, line, message, { persistKey = null } = {}) {
  const { box, text } = errorRefs(component, line);
  if (persistKey) stickyMessage = { key: persistKey, text: message };
  if (!box || !text) return;
  ownWrites.add(text);
  text.textContent = message;
  box.classList.remove('hidden');
}

function clearLineMessage(component, line) {
  stickyMessage = null;
  const { box, text } = errorRefs(component, line);
  if (!box || !text) return;
  ownWrites.add(text);
  text.textContent = '';
  box.classList.add('hidden');
}

function reapplyStickyMessage() {
  if (!stickyMessage) return;
  const row = doc.querySelector(`[data-key="${CSS.escape(stickyMessage.key)}"][data-rod-line]`);
  if (!row) return;
  const component = componentOf(row);
  const line = Number(row.dataset.rodLine || 0);
  const { text } = errorRefs(component, line);
  if (!text || text.textContent.trim()) return;
  showLineMessage(component, line, stickyMessage.text);
}

/** 5.7.5 mapping, reused for /cart/change.js 422 text. */
function mapCartError(raw) {
  const text = String(raw || '').trim();
  if (!text) return '';
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return MSG_OFFLINE;
  if (/you can only add|only \d+|sold out|not enough|is not available|cannot be added|maximum/i.test(text)) {
    const found = text.match(/(\d+)/);
    if (found) return `Bu üründen stokta yalnızca ${found[1]} adet var.`;
    if (/sold out/i.test(text)) return 'Bu ürün şu anda stokta yok.';
    return 'Sepetiniz güncellenemedi. Lütfen tekrar deneyin.';
  }
  if (GENERIC_ERRORS.includes(text.toLowerCase())) return 'Sepetiniz güncellenemedi. Lütfen tekrar deneyin.';
  return text;
}

const translatedErrors = new WeakSet();
function watchLineErrors(component) {
  if (!component || component.dataset.rodErrorsBound === 'true') return;
  component.dataset.rodErrorsBound = 'true';
  const observer = new MutationObserver((records) => {
    for (const record of records) {
      const target = record.target instanceof Element ? record.target : record.target.parentElement;
      const el = target && target.closest ? target.closest('[ref^="cartItemError-"]') : null;
      if (!el) continue;
      if (ownWrites.has(el)) {
        ownWrites.delete(el);
        continue;
      }
      const current = el.textContent || '';
      const mapped = mapCartError(current);
      if (mapped && mapped !== current) {
        ownWrites.add(el);
        el.textContent = mapped;
      }
    }
  });
  observer.observe(component, { subtree: true, characterData: true, childList: true });
}

/* ------------------------------------------------------------------ busy state */
function syncBusy(component) {
  if (!component) return;
  if (isLocked(component)) component.setAttribute('aria-busy', 'true');
  else component.removeAttribute('aria-busy');
}

function watchLock(component) {
  if (!component || component.dataset.rodLockBound === 'true') return;
  component.dataset.rodLockBound = 'true';
  const observer = new MutationObserver(() => {
    syncBusy(component);
    if (!isLocked(component)) flushPendingTaps();
  });
  observer.observe(component, { attributes: true, attributeFilter: ['class'] });
  syncBusy(component);
}

/* ------------------------------------------------------------------ remove / undo */
/** @type {{id:number, quantity:number, properties:object, sellingPlan:number|null}|null} */
let pendingSnapshot = null;
let pendingTimer = null;

function snapshotRow(row) {
  stickyMessage = null;
  if (!row) return;
  // Liquid renders an empty properties hash as `[]`; /cart/add.js rejects an array with 400.
  let properties = {};
  try {
    const parsed = JSON.parse(row.dataset.rodProperties || '{}');
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) properties = parsed;
  } catch (e) {
    properties = {};
  }
  const id = Number(row.dataset.rodVariantId);
  if (!Number.isFinite(id) || id <= 0) return;
  pendingSnapshot = {
    id,
    quantity: Number(row.dataset.rodQty) || 1,
    properties,
    sellingPlan: row.dataset.rodSellingPlan ? Number(row.dataset.rodSellingPlan) : null,
  };
  armRemovalFeedback();
}

function armRemovalFeedback() {
  clearTimeout(pendingTimer);
  doc.removeEventListener('rod:cart:updated', onRemovalSettled);
  doc.addEventListener('rod:cart:updated', onRemovalSettled, { once: true });
  pendingTimer = setTimeout(() => {
    doc.removeEventListener('rod:cart:updated', onRemovalSettled);
    pendingSnapshot = null;
  }, 8000);
}

function onRemovalSettled() {
  clearTimeout(pendingTimer);
  const snapshot = pendingSnapshot;
  pendingSnapshot = null;
  if (!snapshot) return;
  showFeedback({
    message: MSG_REMOVED,
    tone: 'info',
    action: { label: 'Geri al', onClick: () => undo(snapshot) },
  });
}

async function undo(snapshot) {
  if (anyLocked()) return;
  const line = { id: snapshot.id, quantity: snapshot.quantity };
  if (snapshot.properties && Object.keys(snapshot.properties).length) line.properties = snapshot.properties;
  if (snapshot.sellingPlan) line.selling_plan = snapshot.sellingPlan;
  const payload = { items: [line] };

  try {
    const response = await fetch(`${cartAddRoute}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      let description = '';
      try {
        const data = await response.json();
        description = data.description || data.message || '';
      } catch (e) {
        description = '';
      }
      showFeedback({ message: mapCartError(description) || MSG_UNDO_FAILED, tone: 'error' });
      return;
    }
    // The add response is the added line, not the cart: read the live cart for the event detail.
    const cartResponse = await fetch(`${cartRoute}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!cartResponse.ok) throw new Error(`HTTP ${cartResponse.status}`);
    const cartJs = await cartResponse.json();

    const deferred = CartLinesUpdateEvent.createPromise();
    doc.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: String(snapshot.id), quantity: snapshot.quantity }],
        promise: deferred.promise,
      })
    );
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cartJs),
      detail: {
        items: cartJs.items,
        itemCount: cartJs.item_count,
        source: 'rod-cart-undo',
        didError: false,
      },
    });
    showFeedback({ message: MSG_RESTORED, tone: 'success' });
  } catch (error) {
    showFeedback({ message: MSG_OFFLINE, tone: 'error' });
  }
}

/* ------------------------------------------------------------------ feedback (notice / toast) */
function clearNotice() {
  const host = doc.querySelector('[data-rod-cart-notice]');
  if (host) {
    host.textContent = '';
    host.className = 'rod-cart__notice';
  }
}

function showFeedback({ message, tone = 'info', action = null }) {
  const host = openDrawerNotice();
  if (!host) {
    if (Rod && typeof Rod.toast === 'function') {
      Rod.toast({ message, tone, action, timeout: action ? 8000 : 5000 });
    }
    return;
  }
  host.textContent = '';
  host.className = `rod-cart__notice rod-cart__notice--${tone}`;

  const iconName = tone === 'error' ? 'circle-alert' : tone === 'success' ? 'circle-check' : 'info';
  const icon = Rod && typeof Rod.icon === 'function' ? Rod.icon(iconName) : null;
  if (icon instanceof Element) {
    icon.classList.add('rod-cart__notice-icon');
    host.appendChild(icon);
  }

  const text = doc.createElement('p');
  text.className = 'rod-cart__notice-text';
  text.textContent = String(message ?? '');
  host.appendChild(text);

  if (action && action.label) {
    const button = doc.createElement('button');
    button.type = 'button';
    button.className = 'rod-cart__notice-action';
    const undoIcon = Rod && typeof Rod.icon === 'function' ? Rod.icon('undo-2') : null;
    if (undoIcon instanceof Element) button.appendChild(undoIcon);
    const label = doc.createElement('span');
    label.textContent = action.label;
    button.appendChild(label);
    // stopPropagation: clearNotice() detaches this button while the click is still bubbling, and the
    // drawer's panel listener would read the detached target as a backdrop click and close the cart.
    // rod-patch WP-6 (hardening round): the notice used to be cleared BEFORE the undo request was
    // sent, so the coordinates just clicked belonged to whatever the notice was covering for the
    // whole in-flight window and a second click could navigate away mid-POST (see the same fix in
    // rod-core.js `toast`, where it was reproducible on /cart). The notice now survives until the
    // action settles and its button is dead to a second press.
    let actionBusy = false;
    button.addEventListener('click', (event) => {
      event.stopPropagation();
      if (actionBusy || anyLocked()) return;
      actionBusy = true;
      button.setAttribute('aria-disabled', 'true');
      button.setAttribute('aria-busy', 'true');
      // Only clear if this notice is still the one on screen: undo() ends by calling showFeedback()
      // with "Ürün sepete geri eklendi", which replaces the host's children - clearing afterwards
      // would wipe the confirmation the shopper is meant to read.
      const settle = () => {
        if (button.isConnected) clearNotice();
      };
      let result;
      try {
        result = action.onClick?.();
      } catch (e) {
        settle();
        return;
      }
      if (result && typeof result.then === 'function') result.then(settle, settle);
      else settle();
    });
    host.appendChild(button);
    syncNoticeAction();
  }

  const close = doc.createElement('button');
  close.type = 'button';
  close.className = 'rod-cart__notice-close';
  close.setAttribute('aria-label', 'Bildirimi kapat');
  const x = Rod && typeof Rod.icon === 'function' ? Rod.icon('x') : null;
  if (x instanceof Element) close.appendChild(x);
  close.addEventListener('click', (event) => {
    event.stopPropagation();
    clearNotice();
  });
  host.appendChild(close);
}

/** "Geri al" is aria-disabled and ignored while any cart items component is locked. */
function syncNoticeAction() {
  const button = doc.querySelector('.rod-cart__notice-action');
  if (!button) return;
  button.setAttribute('aria-disabled', anyLocked() ? 'true' : 'false');
}

let clearInFlight = false;

async function clearCart(trigger) {
  if (clearInFlight || anyLocked()) return;

  const components = [...doc.querySelectorAll('cart-items-component')];
  const buttons = [...doc.querySelectorAll('[data-rod-cart-clear]')];
  const sectionIds = components.map((component) => component.dataset.sectionId).filter(Boolean);
  const linesById = new Map();
  for (const row of doc.querySelectorAll('[data-rod-line]')) {
    const id = row.dataset.key;
    if (id) linesById.set(id, { id, quantity: 0 });
  }

  clearInFlight = true;
  pendingSnapshot = null;
  clearTimeout(pendingTimer);
  pendingTaps.clear();
  components.forEach((component) => {
    component.classList.add('cart-items-disabled');
    component.setAttribute('aria-busy', 'true');
  });
  buttons.forEach((button) => {
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    const label = button.querySelector('span');
    if (label) label.textContent = 'Temizleniyor…';
  });

  try {
    const response = await fetch(cartClearRoute, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        sections: sectionIds,
        sections_url: `${location.pathname}${location.search}`,
      }),
    });
    const responseText = await response.text();
    let cart;
    try {
      cart = JSON.parse(responseText);
    } catch (error) {
      throw new Error(MSG_CLEAR_FAILED);
    }
    if (!response.ok || cart.errors) {
      throw new Error(mapCartError(cart.description || cart.message || cart.errors) || MSG_CLEAR_FAILED);
    }

    const deferred = CartLinesUpdateEvent.createPromise();
    doc.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'remove',
        context: 'cart',
        lines: [...linesById.values()],
        promise: deferred.promise,
      })
    );
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        sections: cart.sections,
        items: cart.items || [],
        itemCount: 0,
        source: 'rod-cart-clear',
        didError: false,
      },
    });
    showFeedback({ message: MSG_CLEARED, tone: 'success' });
  } catch (error) {
    const message = error instanceof Error ? error.message : MSG_CLEAR_FAILED;
    showFeedback({ message, tone: 'error' });
  } finally {
    clearInFlight = false;
    components.forEach((component) => {
      component.classList.remove('cart-items-disabled');
      component.removeAttribute('aria-busy');
    });
    buttons.forEach((button) => {
      if (!button.isConnected) return;
      button.disabled = false;
      button.removeAttribute('aria-busy');
      const label = button.querySelector('span');
      if (label) label.textContent = 'Sepeti temizle';
    });
    trigger?.blur();
  }
}

/* ------------------------------------------------------------------ quantity rules */
function selectorOf(input) {
  return input.closest('cart-quantity-selector-component');
}

function lastValid(input) {
  return input.dataset.rodLast || input.defaultValue || '1';
}

/**
 * Sanitises a cart line quantity before Horizon's document-capture `setQuantity` runs.
 * @returns {{allow: boolean, value: number|null}}
 */
function sanitize(input) {
  const component = componentOf(input);
  const row = rowOf(input);
  const line = Number((row && row.dataset.rodLine) || input.dataset.cartLine || 0);

  if (isLocked(component)) {
    // Typed during the lock: restore the last committed value, no request, no message (5.11.1).
    input.value = lastValid(input);
    return { allow: false, value: null };
  }

  const parsed = Rod && Rod.qty ? Rod.qty.parse(input.value) : { ok: /^\d+$/.test(String(input.value).trim()) };
  if (!parsed.ok) {
    input.value = lastValid(input);
    showLineMessage(component, line, MSG_INVALID);
    return { allow: false, value: null };
  }

  let value = parsed.value;
  const max = parseInt(input.getAttribute('max') || '', 10);
  if (Number.isFinite(max) && value > max) {
    value = max;
    showLineMessage(component, line, `Bu üründen stokta yalnızca ${max} adet var.`, { persistKey: row?.dataset.key || null });
  } else {
    clearLineMessage(component, line);
  }

  input.value = String(value);
  if (value === 0) snapshotRow(row);
  else input.dataset.rodLast = String(value);
  return { allow: true, value };
}

window.addEventListener(
  'blur',
  (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.matches(QTY_INPUT)) return;
    if (!componentOf(target)) return;
    const result = sanitize(target);
    if (!result.allow) {
      event.preventDefault();
      event.stopPropagation();
    }
  },
  true
);

window.addEventListener(
  'keydown',
  (event) => {
    if (event.key !== 'Enter' || event.isComposing) return;
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.matches(QTY_INPUT)) return;
    if (!componentOf(target)) return;
    // Never let Enter submit the cart form (it carries the checkout submit button).
    event.preventDefault();
    event.stopPropagation();
    const result = sanitize(target);
    if (!result.allow) return;
    const selector = selectorOf(target);
    if (selector && typeof selector.onQuantityChange === 'function') {
      selector.onQuantityChange();
      selector.updateButtonStates?.();
    }
  },
  true
);

/* ------------------------------------------------------------------ lock: clicks and taps */
/** @type {Map<string, {input: HTMLInputElement, target: number}>} */
const pendingTaps = new Map();
let tapTimer = null;
/** Per stepper: the moment after which its previous quantity click counts as settled (see below). */
const qtySettling = new WeakMap();
const QTY_SETTLE_MS = 700;

function accumulateTap(button) {
  const selector = button.closest('cart-quantity-selector-component');
  const input = selector ? selector.querySelector(QTY_INPUT) : null;
  if (!input) return;
  const row = rowOf(input);
  const key = (row && row.dataset.key) || (row && row.dataset.rodLine) || String(pendingTaps.size);
  const delta = button.getAttribute('name') === 'minus' ? -1 : 1;
  const current = pendingTaps.get(key);
  const base = current ? current.target : parseInt(input.value, 10) || 0;
  const max = parseInt(input.getAttribute('max') || '', 10);
  let target = base + delta;
  if (target < 0) target = 0;
  if (Number.isFinite(max) && target > max) target = max;
  pendingTaps.set(key, { input, target });
  clearTimeout(tapTimer);
  tapTimer = setTimeout(flushPendingTaps, 400);
}

function flushPendingTaps() {
  if (!pendingTaps.size) return;
  if (anyLocked()) {
    clearTimeout(tapTimer);
    tapTimer = setTimeout(flushPendingTaps, 400);
    return;
  }
  for (const [key, entry] of pendingTaps) {
    pendingTaps.delete(key);
    const { input, target } = entry;
    if (!input.isConnected) continue;
    if (String(target) === input.value) continue;
    input.value = String(target);
    if (target === 0) snapshotRow(rowOf(input));
    else input.dataset.rodLast = String(target);
    const selector = selectorOf(input);
    if (selector && typeof selector.onQuantityChange === 'function') {
      selector.onQuantityChange();
      selector.updateButtonStates?.();
    }
    // One change at a time: Horizon serialises through the component lock.
    break;
  }
  if (pendingTaps.size) {
    clearTimeout(tapTimer);
    tapTimer = setTimeout(flushPendingTaps, 400);
  }
}

window.addEventListener(
  'click',
  (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const remove = target.closest('[data-rod-line-remove]');
    const qtyButton = target.closest('.rod-cart__qty-btn');
    const checkout = target.closest('.rod-cart__checkout, .rod-cart__bar-cta');
    const clear = target.closest('[data-rod-cart-clear]');
    if (clear) {
      event.preventDefault();
      event.stopImmediatePropagation();
      clearCart(clear);
      return;
    }
    const control = remove || qtyButton || checkout;
    if (!control) return;

    const component = componentOf(control);
    if (isLocked(component)) {
      // pointer-events: none already blocks the mouse; this closes the keyboard hole (5.11.1).
      event.preventDefault();
      event.stopImmediatePropagation();
      if (qtyButton) accumulateTap(qtyButton);
      return;
    }

    if (remove) snapshotRow(rowOf(remove));

    // "minus at 1" removes the line through Horizon's own path: snapshot first so undo works.
    if (qtyButton && qtyButton.getAttribute('name') === 'minus') {
      const selector = qtyButton.closest('cart-quantity-selector-component');
      const input = selector ? selector.querySelector(QTY_INPUT) : null;
      const value = input ? parseInt(input.value, 10) || 0 : 0;
      // rod-patch WP-6 (hardening round): a FAST double-click on "Adedi azalt" at quantity 2 used to
      // be processed as 2 -> 1 and then 1 -> remove, because Horizon only takes the component lock
      // once its request is in flight and the second click arrives before that. Deleting a line is
      // destructive and must be a deliberate, separate press, so a minus that would empty the line
      // is swallowed while the shopper's own previous quantity change is still settling. Anything
      // slower than the window below still removes the line exactly as before, and the guard never
      // touches "+" or a minus that only decrements.
      if (input && value <= 1 && selector && Date.now() < (qtySettling.get(selector) || 0)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      if (input && value <= 1) snapshotRow(rowOf(input));
    }
    if (qtyButton) {
      const selector = qtyButton.closest('cart-quantity-selector-component');
      if (selector) qtySettling.set(selector, Date.now() + QTY_SETTLE_MS);
    }
  },
  true
);

/* ------------------------------------------------------------------ cart page bottom bar */
let barObserver = null;
let keyboardOpen = false;

function cartBar() {
  return doc.querySelector('[data-rod-cart-bar]');
}

/** @param {Element} el */
function isInViewport(el) {
  const rect = el.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return false;
  const height = window.innerHeight || doc.documentElement.clientHeight;
  return rect.bottom > 0 && rect.top < height;
}

function updateBar() {
  const bar = cartBar();
  if (!bar) {
    Rod?.bottomUi?.clear('cart-bar');
    return;
  }
  const wide = window.matchMedia('(min-width: 990px)').matches;
  const sourceVisible = bar.dataset.rodSourceVisible === 'true';
  const show = !wide && !sourceVisible && !keyboardOpen;
  if (show) {
    bar.hidden = false;
    const height = Math.round(bar.getBoundingClientRect().height);
    if (height > 0) Rod?.bottomUi?.set('cart-bar', height);
  } else {
    bar.hidden = true;
    Rod?.bottomUi?.clear('cart-bar');
  }
}

function setupBar() {
  const bar = cartBar();
  if (!bar) {
    barObserver?.disconnect();
    barObserver = null;
    Rod?.bottomUi?.clear('cart-bar');
    return;
  }
  if (bar.dataset.rodBarBound === 'true') {
    updateBar();
    return;
  }
  bar.dataset.rodBarBound = 'true';

  const source = doc.querySelector('.rod-cart-page [data-rod-sticky-source], .cart-page [data-rod-sticky-source]');
  // Seed the state synchronously: IntersectionObserver delivers its first callback asynchronously,
  // so assuming "hidden source" here would flash the fixed bar for a frame on short carts and
  // bounce --rod-bottom-ui 0 → 65 → 0 (which also nudges the page wrapper padding).
  bar.dataset.rodSourceVisible = source && isInViewport(source) ? 'true' : 'false';

  barObserver?.disconnect();
  if (source && 'IntersectionObserver' in window) {
    barObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) bar.dataset.rodSourceVisible = entry.isIntersecting ? 'true' : 'false';
        updateBar();
      },
      { threshold: 0 }
    );
    barObserver.observe(source);
  }
  updateBar();
}

doc.addEventListener('focusin', (event) => {
  const target = event.target;
  if (target instanceof HTMLElement && target.matches('input:not([type="checkbox"]):not([type="radio"]), textarea')) {
    keyboardOpen = true;
    updateBar();
  }
});
doc.addEventListener('focusout', () => {
  keyboardOpen = false;
  setTimeout(updateBar, 50);
});
window.addEventListener('resize', () => updateBar());

/* ------------------------------------------------------------------ back link */
doc.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const link = target ? target.closest('[data-rod-cart-back]') : null;
  if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) return;
  let referrer = null;
  try {
    referrer = doc.referrer ? new URL(doc.referrer) : null;
  } catch (e) {
    referrer = null;
  }
  if (!referrer || referrer.origin !== location.origin) return;
  if (referrer.pathname.replace(/\/$/, '') === location.pathname.replace(/\/$/, '')) return;
  event.preventDefault();
  history.back();
});

/* ------------------------------------------------------------------ boot */
function bindAll() {
  for (const component of doc.querySelectorAll('cart-items-component')) {
    watchLock(component);
    watchLineErrors(component);
  }
  reapplyStickyMessage();
  setupBar();
  syncNoticeAction();
  observeCartRoots();
}

const bindSoon = Rod.debounce(bindAll, 120);

doc.addEventListener('rod:cart:updated', () => {
  bindAll();
  syncNoticeAction();
});
doc.addEventListener('theme-drawer:close', clearNotice);
doc.addEventListener('theme-drawer:open', bindSoon);
window.addEventListener('pageshow', bindSoon);

/* This module loads on every page (the drawer is site-wide). Observing document.body would make
   every unrelated mutation (rails hydrating, filter re-renders, header updates) schedule bindAll()
   and its getBoundingClientRect() work, so only the cart section wrappers are observed. The wrappers
   survive Horizon's section renders; their contents are what gets replaced. */
const rootObserver = new MutationObserver(bindSoon);
/** @type {Element[]} */
let observedRoots = [];

function cartRoots() {
  /** @type {Set<Element>} */
  const roots = new Set();
  for (const el of doc.querySelectorAll('cart-drawer-component, cart-items-component, [data-rod-cart-page]')) {
    roots.add(el.closest('.shopify-section') || el.parentElement || el);
  }
  return [...roots];
}

function observeCartRoots() {
  const roots = cartRoots();
  if (roots.length === observedRoots.length && roots.every((root, i) => root === observedRoots[i])) return;
  rootObserver.disconnect();
  observedRoots = roots;
  for (const root of roots) rootObserver.observe(root, { childList: true, subtree: true });
}

function init() {
  bindAll();
}

if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', init, { once: true });
else init();
